import { IMultiSectionUnitConfiguration } from '../Models/IUnitConfiguration';
import { IMultiSectionProperties } from '../Models/IMultiSectionProperties';
import { IDesignRangeFeedback } from '../Models/IDesignRangeFeedback';
import { getLocalStorageItem, getPreferredUnits } from '../Services/Global';
import { Constants } from '../Models/Constants';

/**
 * Interface for explicitly passed form data values (used in Single Section mode)
 */
export interface IExplicitFormData {
  coldMediaFlowrate?: string;
  coldMediaTempIn?: string;
  coldMediaTempOut?: string;
  coldMediaPressureDrop?: string;
  coldMediaWallTemp?: string;
  hotMediaFlowrate?: string;
  hotMediaTempIn?: string;
  hotMediaTempOut?: string;
  hotMediaPressureDrop?: string;
  hotMediaWallTemp?: string;
}

/**
 * Decimal places kept on numeric values written to the report.
 *
 * Values arrive here as exact conversion results, because UnitAwareTextField
 * deliberately does not round — rounding at input would break the display round
 * trip, so a typed 65 GPM would come back as 64.99. That exactness is
 * meaningless in the report, where 65 GPM becomes 14.763105958614238 m3/h and
 * implies a precision nobody entered. Four decimals removes the noise and still
 * reproduces the typed value at the two-decimal precision the UI shows.
 */
const REPORT_DECIMALS = 4;

const roundForReport = (value: number): number => parseFloat(value.toFixed(REPORT_DECIMALS));

/**
 * The form's placeholder for "this target has no configured unit". It is not a
 * unit and must never be reported as one.
 */
const NO_UNIT_PLACEHOLDER = 'Integer';

/**
 * Custom hook to collect complete form data for design range feedback submission.
 * Captures all form fields including unit configuration and user inputs.
 * 
 * @param unitConfiguration - The unit configuration containing industry, position, etc.
 * @param userInputs - Array of user input properties (primary source for Multi-Section)
 * @param explicitValues - Object containing direct form values (primary source for Single-Section)
 * @returns A function that when called, returns the collected form data. Can optionally accept a targetSectionNumber.
 */
export function useFormDataCollector(
  unitConfiguration: IMultiSectionUnitConfiguration | undefined,
  userInputs: IMultiSectionProperties[] | undefined,
  explicitValues?: IExplicitFormData
): (targetSectionNumber?: number) => Partial<IDesignRangeFeedback> {
  
  return (targetSectionNumber?: number) => {
    if (!unitConfiguration) {
      console.warn('useFormDataCollector: unitConfiguration is undefined');
      return {};
    }

    // Helper to safely parse string/number to number, rounded for reporting
    const safeParse = (val: string | number | undefined | null): number | undefined => {
      if (val === undefined || val === null || val === '') return undefined;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? undefined : roundForReport(num);
    };

    // Helper function to safely get numeric value from inputs
    // Priority:
    // 1. explicitValues (if provided and matches propertyName) - used for Single Section
    // 2. userInputs (search by name/constant) - used for Multi Section
    const getUserInputValue = (propertyName: string, constantName: string, overrideSectionNumber?: number): number | undefined => {
      
      // 1. Try explicit values first (for Single Section)
      if (!unitConfiguration?.isMultiSection && explicitValues) {
        const explicitKey = propertyName as keyof IExplicitFormData;
        if (explicitValues[explicitKey] !== undefined) {
             return safeParse(explicitValues[explicitKey]);
        }
      }

      // 2. Fallback to userInputs (for Multi Section)
      // Determine which section number to use for filtering
      // Priority: overrideSectionNumber > targetSectionNumber > unitConfiguration.sectionNumber
      const sectionToUse = overrideSectionNumber !== undefined 
        ? overrideSectionNumber 
        : (targetSectionNumber !== undefined ? targetSectionNumber : unitConfiguration.sectionNumber);

      // Check both trimmedName and name against propertyName and constantName to be robust
      const input = userInputs?.find(x => 
        (x.trimmedName === propertyName || x.trimmedName === constantName || x.name === constantName || x.name === propertyName) && 
        (sectionToUse === undefined || x.sectionNumber === sectionToUse)
      );
      
      return safeParse(input?.value);
    };

    // The target's name and unit live on the per-section configuration, which
    // this hook never receives. The property built from that configuration does
    // carry them, so fall back to it when the root configuration has nothing —
    // which is the case for every multi-section position, and is why these two
    // columns were arriving as NULL.
    const targetProperty = userInputs?.find(x => x.name === Constants.UnitOperationTargetValue);

    const targetUnitFromProperty =
      targetProperty?.flowUnit && targetProperty.flowUnit !== NO_UNIT_PLACEHOLDER
        ? targetProperty.flowUnit
        : undefined;

    const formData: Partial<IDesignRangeFeedback> = {
      // Configuration fields
      industry: unitConfiguration.industry,
      subIndustry: unitConfiguration.subIndustry,
      application: unitConfiguration.application,
      subApplication: unitConfiguration.subApplication,
      processStage: unitConfiguration.processStage,
      breakDownOfProcessStage: unitConfiguration.breakdownOfProcessStage,
      alfaLavalPosition: unitConfiguration.alfaLavalPosition,
      productLine: unitConfiguration.productLine,
      flowType: unitConfiguration.flowType,
      country: getLocalStorageItem('country') || 'Unknown',
      
      // Section information
      sectionCount: unitConfiguration.sectionCount,
      // If targetSectionNumber is provided, use it as the section number for this record
      sectionNumber: targetSectionNumber !== undefined ? targetSectionNumber : unitConfiguration.sectionNumber,
      
      // Unit operation target
      unitOperationTargetName: unitConfiguration.unitOperationTargetName ?? targetProperty?.property,
      unitOperationTargetValueUnit: unitConfiguration.unitOperationTargetValueUnit ?? targetUnitFromProperty,
      // Pass 0 as overrideSectionNumber because UnitOperationTargetValue is always in section 0
      unitOperationTargetValue: getUserInputValue(Constants.UnitOperationTargetValue, Constants.UnitOperationTargetValue, 0),
      
      // Media types
      coldMediaType: unitConfiguration.coldMediaType,
      hotMediaType: unitConfiguration.hotMediaType,
      
      // Cold media values
      coldMediaFlowrate: getUserInputValue('coldMediaFlowrate', Constants.ColdMediaFlowrate),
      coldMediaTempIn: getUserInputValue('coldMediaTempIn', Constants.ColdMediaTempIn),
      coldMediaTempOut: getUserInputValue('coldMediaTempOut', Constants.ColdMediaTempOut),
      coldMediaPressureDrop: getUserInputValue('coldMediaPressureDrop', Constants.ColdMediaPressureDrop),
      coldMediaWallTemp: getUserInputValue('coldMediaWallTemp', Constants.ColdMediaWallTemp),
      
      // Hot media values
      hotMediaFlowrate: getUserInputValue('hotMediaFlowrate', Constants.HotMediaFlowrate),
      hotMediaTempIn: getUserInputValue('hotMediaTempIn', Constants.HotMediaTempIn),
      hotMediaTempOut: getUserInputValue('hotMediaTempOut', Constants.HotMediaTempOut),
      hotMediaPressureDrop: getUserInputValue('hotMediaPressureDrop', Constants.HotMediaPressureDrop),
      hotMediaWallTemp: getUserInputValue('hotMediaWallTemp', Constants.HotMediaWallTemp),
      
      // Browser metadata
      browserInfo: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,

      // Snapshot, not a live lookup: the user may change this setting later,
      // and the report must record what was in effect at submission time.
      unitSystem: getPreferredUnits().preferredUnits
    };
    
    return formData;
  };
}
